import React, { useMemo, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import ReactApexChart from 'react-apexcharts'

import { httpOk, GET } from 'helpers/api'
import { formatMoney, formatDate } from 'helpers/helpers'
import { buildAnalyticsUrl } from 'pages/main/analytics/parts/analyticsDates'
import Rightbar from 'pages/main/cheques/Rightbar'
import ChainFilterBar, { useChainFilters, ExcelButton } from '../parts/ChainFilterBar'
import { downloadExcel, periodText } from '../parts/chainExcel'
import {
	percent, decimal, columnStats, heatStyle, buildRanks, chainColor,
	RankBadge, Delta, FillCell, useSort, SortTh, ChainKpi, HeatLegend, ChainEmpty,
} from '../parts/chainUi'

// Цвет оценки: 1–2 плохо, 3 нейтрально, 4–5 хорошо
const RATING_COLORS = { 1: '#f06548', 2: '#f59f00', 3: '#adb5bd', 4: '#5fc9a0', 5: '#12b886' }
const RATINGS = [1, 2, 3, 4, 5]

function Stars({ value }) {
	return (
		<span className="text-nowrap" style={{ color: RATING_COLORS[value], fontWeight: 700 }}>
			{value}<i className="uil uil-star ms-1"></i>
		</span>
	)
}

// Полоса распределения 1–5: каждый отрезок кликабелен — открывает эти оценки
function RatingSplit({ row, onPick }) {
	const { t } = useTranslation()
	const total = row.ratings || 0
	if (total === 0) return <span className="chain-muted">—</span>
	return (
		<>
			<div className="chain-split">
				{RATINGS.map(value => row[`r${value}`] > 0 && (
					<span key={value}
						className="chain-click"
						title={`${value} ★: ${formatMoney(row[`r${value}`])} (${percent(row[`r${value}`] / total * 100)})`}
						style={{ width: `${row[`r${value}`] / total * 100}%`, background: RATING_COLORS[value] }}
						onClick={() => onPick([value])}>
					</span>
				))}
			</div>
			<div className="chain-muted mt-1 text-nowrap" title={t('chain_rating_split_hint')}>
				{RATINGS.map(value => formatMoney(row[`r${value}`])).join(' · ')}
			</div>
		</>
	)
}

function ChequeRating() {
	const { t } = useTranslation()
	const [data, setData] = useState(null)
	const [details, setDetails] = useState(null)
	const [cheque, setCheque] = useState({})
	const [showRightBar, setShowRightBar] = useState(false)

	const filters = useChainFilters({}, searchWithFilter)
	const { applied } = filters

	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl('/chain/report/ratings', filter), {}, { loader: false })
		if (httpOk(response)) setData(response.data)
	}

	const branches = useMemo(() => data?.branches || [], [data])
	const totals = data?.totals || {}
	const { sorted, sort, toggle } = useSort(branches, 'avg')
	// Место — по средней, среди филиалов, где оценки были
	const { ranks, count } = useMemo(() => buildRanks(branches.filter(item => item.ratings > 0), 'avg', true), [branches])

	const stats = useMemo(() => ({
		avg: columnStats(branches, 'avg'),
		bad_percent: columnStats(branches, 'bad_percent'),
		good_percent: columnStats(branches, 'good_percent'),
		coverage: columnStats(branches, 'coverage'),
	}), [branches])
	const maxRatings = useMemo(() => columnStats(branches, 'ratings').max, [branches])

	const rated = branches.filter(item => item.ratings > 0)
	const best = rated.length > 0 ? [...rated].sort((a, b) => b.avg - a.avg || b.ratings - a.ratings)[0] : null
	const worst = rated.length > 1 ? [...rated].sort((a, b) => a.avg - b.avg || b.ratings - a.ratings)[0] : null

	async function openDetails(posId, ratings, withNote = false) {
		const response = await GET(buildAnalyticsUrl('/chain/report/ratings-details', applied, {
			pos_id: posId || null,
			ratings: ratings && ratings.length ? ratings : null,
			with_note: withNote ? 1 : null,
		}), {}, { loader: true })
		if (httpOk(response)) {
			const branch = branches.find(item => item.pos_id === posId)
			setDetails({
				...response.data,
				pos_name: branch ? branch.pos_name : t('chain_all_branches'),
				label: withNote ? t('chain_ratings_with_comment') : ratings && ratings.length ? ratings.map(value => `${value} ★`).join(', ') : t('chain_all_ratings'),
			})
		}
	}

	async function openCheque(row) {
		if (!row.cheque_found) return
		const response = await GET(`/cheques/${row.cheque_id}`, {}, { loader: true })
		if (httpOk(response)) {
			setCheque({ ...response.data.data, showSidebar: true })
			setShowRightBar(true)
		}
	}

	const ratingColumns = () => [
		{ label: t('date'), type: 'text', width: 17 },
		{ label: t('chain_branch'), type: 'text', width: 26 },
		{ label: t('rating'), type: 'int', width: 8 },
		{ label: t('comment'), type: 'text', width: 40 },
		{ label: t('client'), type: 'text', width: 20 },
		{ label: t('phone'), type: 'text', width: 15 },
		{ label: t('cheque'), type: 'text', width: 10 },
		{ label: t('amount'), type: 'money' },
		{ label: t('waiter'), type: 'text', width: 18 },
		{ label: t('courier'), type: 'text', width: 18 },
		{ label: t('chain_rating_solution'), type: 'text', width: 36 },
		{ label: t('chain_rating_solution_date'), type: 'text', width: 17 },
	]
	const ratingRow = (row) => [
		formatDate(row.created_at, 'dd.MM.yyyy HH:mm'), row.pos_name, row.rating, row.note || '', row.client_name || '', row.client_phone || '',
		row.cheque_number ? `№${row.cheque_number}` : '', row.total_amount_service, row.waiter_name || '', row.courier_name || '',
		row.solution || '', row.solution_date ? formatDate(row.solution_date, 'dd.MM.yyyy HH:mm') : '',
	]

	function exportExcel() {
		if (!data) return
		const period = `${t('period')}: ${periodText(data.start_date, data.end_date)}`
		const byAvg = buildRanks(branches.filter(item => item.ratings > 0), 'avg', true).ranks
		const branchRow = (item, rank) => [
			rank, item.pos_name, item.ratings, item.avg, item.prev_avg, item.avg_diff,
			item.r1, item.r2, item.r3, item.r4, item.r5, item.bad_percent, item.good_percent,
			item.notes, item.bad_unsolved, item.cheques, item.coverage, item.client_coverage,
		]
		const daily = data.daily || { days: [] }

		downloadExcel(`${t('chain_ratings_title')} ${periodText(data.start_date, data.end_date)}`, [
			{
				name: t('chain_branches'),
				title: t('chain_ratings_title'),
				subtitle: period,
				columns: [
					{ label: t('chain_rank'), type: 'int', width: 8 },
					{ label: t('chain_branch'), type: 'text', width: 30 },
					{ label: t('chain_ratings_count'), type: 'int', width: 10 },
					{ label: t('average_rating'), type: 'number', width: 10 },
					{ label: t('chain_prev_period'), type: 'number', width: 11 },
					{ label: t('chain_rating_diff'), type: 'number', width: 11 },
					{ label: '1 ★', type: 'int', width: 7 },
					{ label: '2 ★', type: 'int', width: 7 },
					{ label: '3 ★', type: 'int', width: 7 },
					{ label: '4 ★', type: 'int', width: 7 },
					{ label: '5 ★', type: 'int', width: 7 },
					{ label: t('chain_bad_share'), type: 'percent', width: 11 },
					{ label: t('chain_good_share'), type: 'percent', width: 11 },
					{ label: t('comments'), type: 'int', width: 11 },
					{ label: t('chain_bad_unsolved'), type: 'int', width: 13 },
					{ label: t('cheques'), type: 'int', width: 10 },
					{ label: t('chain_coverage'), type: 'percent', width: 10 },
					{ label: t('chain_client_coverage'), type: 'percent', width: 14 },
				],
				rows: [...branches].sort((a, b) => (b.avg ?? -1) - (a.avg ?? -1)).map(item => branchRow(item, byAvg[item.pos_id] || '')),
				footer: branchRow({ ...totals, pos_name: t('total') }, ''),
			},
			{
				name: t('chain_by_days'),
				title: `${t('chain_ratings_title')} — ${t('chain_by_days')}`,
				subtitle: period,
				columns: [
					{ label: t('date'), type: 'text', width: 12 },
					{ label: t('chain_ratings_count'), type: 'int', width: 10 },
					{ label: t('average_rating'), type: 'number', width: 10 },
					{ label: t('chain_bad_ratings'), type: 'int', width: 12 },
				],
				rows: daily.days.map((day, index) => [day.split('-').reverse().join('.'), daily.ratings[index], daily.avg[index], daily.bad[index]]),
			},
			{
				name: t('chain_bad_ratings'),
				title: `${t('chain_bad_ratings')} (1–2 ★)`,
				subtitle: period,
				columns: ratingColumns(),
				rows: (data.bad || []).map(ratingRow),
			},
		])
	}

	function exportDetails() {
		if (!details) return
		downloadExcel(`${t('chain_ratings_title')} — ${details.pos_name} — ${details.label}`, [{
			name: t('rating'),
			title: `${details.pos_name} — ${details.label}`,
			subtitle: `${t('period')}: ${periodText(applied.start_date, applied.end_date)}`,
			columns: ratingColumns(),
			rows: details.rows.map(ratingRow),
		}])
	}

	return (
		<>
			<ChainFilterBar
				title={t('chain_ratings_title')}
				icon="uil-star"
				filters={filters}
				onExcel={exportExcel}
				excelDisabled={!data}
			/>

			<div className="row g-3 mb-3">
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('average_rating')} icon="uil-star" color="#f59f00"
						value={totals.avg !== null && totals.avg !== undefined ? decimal(totals.avg) : '—'}
						sub={<><Delta value={totals.avg_delta} /> {t('chain_vs_prev')}</>} />
				</div>
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('chain_ratings_count')} icon="uil-comment-alt-check" color="#5b73e8"
						value={formatMoney(totals.ratings || 0)}
						sub={<>{t('chain_coverage')} <b>{percent(totals.coverage, 2)}</b></>}
						title2={t('chain_coverage_hint')} />
				</div>
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('chain_bad_share')} icon="uil-frown" color="#f06548"
						value={percent(totals.bad_percent)}
						sub={<>{formatMoney(totals.bad || 0)} {t('chain_pcs')} · {t('chain_bad_unsolved')} <b>{formatMoney(totals.bad_unsolved || 0)}</b></>} />
				</div>
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('chain_good_share')} icon="uil-smile" color="#12b886"
						value={percent(totals.good_percent)}
						sub={<>4–5 ★ · {formatMoney((totals.r4 || 0) + (totals.r5 || 0))} {t('chain_pcs')}</>} />
				</div>
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('chain_best_branch')} icon="uil-trophy" color="#12b886"
						value={best ? best.pos_name : '—'}
						sub={best ? <>{decimal(best.avg)} ★ · {formatMoney(best.ratings)} {t('chain_ratings_lower')}</> : null} />
				</div>
				<div className="col-xl-2 col-md-4 col-6">
					<ChainKpi title={t('chain_worst_branch')} icon="uil-arrow-down-right" color="#f06548"
						value={worst ? worst.pos_name : '—'}
						sub={worst ? <>{decimal(worst.avg)} ★ · {formatMoney(worst.ratings)} {t('chain_ratings_lower')}</> : null} />
				</div>
			</div>

			<div className="card chain-card mb-3">
				<div className="card-body">
					<div className="chain-toolbar">
						<div className="chain-toolbar-group">
							<h5 className="mb-0 me-2">{t('chain_rating')}</h5>
							<span className="chain-muted">{t('chain_rank_by_avg_rating')}</span>
						</div>
						<HeatLegend />
					</div>

					<div className="chain-scroll">
						<table className="chain-table">
							<thead>
								<tr>
									<th className="chain-index chain-sticky">#</th>
									<SortTh sortKey="pos_name" sort={sort} onSort={toggle} className="chain-sticky-2">{t('chain_branch')}</SortTh>
									<SortTh sortKey="ratings" sort={sort} onSort={toggle} className="text-end">{t('chain_ratings_count')}</SortTh>
									<SortTh sortKey="avg" sort={sort} onSort={toggle} className="text-end">{t('average_rating')}</SortTh>
									<th style={{ minWidth: '170px' }} title={t('chain_rating_split_hint')}>{t('chain_rating_split')}</th>
									<SortTh sortKey="bad_percent" sort={sort} onSort={toggle} className="text-end" title={t('chain_bad_share_hint')}>{t('chain_bad_share')}</SortTh>
									<SortTh sortKey="good_percent" sort={sort} onSort={toggle} className="text-end">{t('chain_good_share')}</SortTh>
									<SortTh sortKey="notes" sort={sort} onSort={toggle} className="text-end">{t('comments')}</SortTh>
									<SortTh sortKey="bad_unsolved" sort={sort} onSort={toggle} className="text-end" title={t('chain_bad_unsolved_hint')}>{t('chain_bad_unsolved')}</SortTh>
									<SortTh sortKey="cheques" sort={sort} onSort={toggle} className="text-end">{t('cheques')}</SortTh>
									<SortTh sortKey="coverage" sort={sort} onSort={toggle} className="text-end" title={t('chain_coverage_hint')}>{t('chain_coverage')}</SortTh>
								</tr>
							</thead>
							<tbody>
								{sorted.length === 0 && <ChainEmpty colSpan={11} />}
								{sorted.map(item => {
									const index = branches.indexOf(item)
									return (
										<tr key={item.pos_id}>
											<td className="chain-index chain-sticky"><RankBadge rank={ranks[item.pos_id]} count={count} /></td>
											<td className="chain-name chain-sticky-2">
												<span className="d-inline-flex align-items-center">
													<span className="chain-dot" style={{ background: chainColor(index) }}></span>
													{item.pos_name}
												</span>
											</td>
											<FillCell percent={maxRatings > 0 ? item.ratings / maxRatings * 100 : 0} tone="primary"
												onClick={item.ratings > 0 ? () => openDetails(item.pos_id, null) : undefined}
												title={item.ratings > 0 ? t('chain_open_details') : undefined}>
												<b>{formatMoney(item.ratings)}</b>
												<div><Delta value={item.ratings_delta} showNone={false} /></div>
											</FillCell>
											<td className="chain-num" style={heatStyle(item.avg, stats.avg)}>
												<b>{item.avg !== null ? decimal(item.avg) : '—'}</b>
												{item.avg_diff !== null && (
													<div title={`${t('chain_prev_period')}: ${decimal(item.prev_avg)}`}>
														<Delta value={item.avg_delta} />
													</div>
												)}
											</td>
											<td><RatingSplit row={item} onPick={(ratings) => openDetails(item.pos_id, ratings)} /></td>
											<td className={`chain-num ${item.bad > 0 ? 'chain-click' : ''}`} style={heatStyle(item.bad_percent, stats.bad_percent, false)}
												onClick={() => item.bad > 0 && openDetails(item.pos_id, [1, 2])}>
												<span>{percent(item.bad_percent)}</span>
												<div className="chain-muted">{formatMoney(item.bad)} {t('chain_pcs')}</div>
											</td>
											<td className="chain-num" style={heatStyle(item.good_percent, stats.good_percent)}>{percent(item.good_percent)}</td>
											<td className={`chain-num ${item.notes > 0 ? 'chain-click' : ''}`}
												onClick={() => item.notes > 0 && openDetails(item.pos_id, null, true)}>
												<span>{item.notes > 0 ? formatMoney(item.notes) : '—'}</span>
											</td>
											<td className="chain-num" style={item.bad_unsolved > 0 ? { color: '#f06548', fontWeight: 700 } : undefined}>
												{item.bad > 0 ? formatMoney(item.bad_unsolved) : '—'}
											</td>
											<td className="chain-num">{formatMoney(item.cheques)}</td>
											<td className="chain-num" style={heatStyle(item.coverage, stats.coverage)}
												title={`${t('chain_client_coverage')}: ${percent(item.client_coverage, 2)}`}>
												{percent(item.coverage, 2)}
											</td>
										</tr>
									)
								})}
							</tbody>
							{sorted.length > 0 && (
								<tfoot>
									<tr>
										<td className="chain-sticky"></td>
										<td className="chain-sticky-2">{t('total')}</td>
										<td className="chain-num chain-click" onClick={() => totals.ratings > 0 && openDetails(null, null)}>{formatMoney(totals.ratings)}</td>
										<td className="chain-num">{totals.avg !== null ? decimal(totals.avg) : '—'}</td>
										<td><RatingSplit row={totals} onPick={(ratings) => openDetails(null, ratings)} /></td>
										<td className="chain-num">{percent(totals.bad_percent)}</td>
										<td className="chain-num">{percent(totals.good_percent)}</td>
										<td className="chain-num">{formatMoney(totals.notes)}</td>
										<td className="chain-num">{formatMoney(totals.bad_unsolved)}</td>
										<td className="chain-num">{formatMoney(totals.cheques)}</td>
										<td className="chain-num">{percent(totals.coverage, 2)}</td>
									</tr>
								</tfoot>
							)}
						</table>
					</div>
					<div className="chain-muted mt-2">
						<i className="uil uil-info-circle me-1"></i>
						{t('chain_ratings_note')}
					</div>
				</div>
			</div>

			<div className="row g-3 mb-3">
				<div className="col-xl-5">
					<DailyChart daily={data?.daily} />
				</div>
				<div className="col-xl-7">
					<div className="card chain-card mb-0 h-100">
						<div className="card-body">
							<div className="chain-card-title">
								<h5>{t('chain_bad_ratings_latest')}</h5>
								{(data?.bad || []).length > 0 && (
									<button type="button" className="btn btn-sm btn-outline-primary" style={{ borderRadius: '10px' }}
										onClick={() => openDetails(null, [1, 2])}>
										{t('chain_show_all')}
									</button>
								)}
							</div>
							<div className="chain-scroll" style={{ maxHeight: '360px' }}>
								<RatingTable rows={(data?.bad || []).slice(0, 30)} onCheque={openCheque} compact />
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* MODAL DETAILS */}
			<Modal show={!!details} onHide={() => setDetails(null)} size="xl" centered scrollable>
				<Modal.Header closeButton>
					<Modal.Title>
						{details?.pos_name} — {details?.label}
						<div className="chain-muted fw-normal">{periodText(applied.start_date, applied.end_date)} · {formatMoney(details?.rows?.length || 0)} {t('chain_ratings_lower')}</div>
					</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ height: '520px' }}>
					{details?.limited && (
						<div className="chain-note mb-2"><i className="uil uil-exclamation-triangle"></i>{t('chain_details_limited')}</div>
					)}
					<RatingTable rows={details?.rows || []} onCheque={openCheque} />
				</Modal.Body>
				<Modal.Footer>
					<ExcelButton onClick={exportDetails} disabled={!details?.rows?.length} />
					<button type="button" className="btn btn-outline-secondary" onClick={() => setDetails(null)}>{t('close')}</button>
				</Modal.Footer>
			</Modal>

			<Rightbar showRightBar={showRightBar} setShowRightBar={setShowRightBar} selectedItem={cheque} />
		</>
	)
}

// Список оценок: расшифровка и «последние плохие»
function RatingTable({ rows, onCheque, compact = false }) {
	const { t } = useTranslation()
	const colSpan = compact ? 6 : 9
	return (
		<table className="chain-table">
			<thead>
				<tr>
					<th>{t('date')}</th>
					<th>{t('chain_branch')}</th>
					<th>{t('rating')}</th>
					<th>{t('comment')}</th>
					<th>{t('client')}</th>
					<th>{t('cheque')}</th>
					{!compact && <th>{t('waiter')}</th>}
					{!compact && <th>{t('courier')}</th>}
					{!compact && <th>{t('chain_rating_solution')}</th>}
				</tr>
			</thead>
			<tbody>
				{rows.length === 0 && <ChainEmpty colSpan={colSpan} />}
				{rows.map(row => (
					<tr key={row.id}>
						<td className="text-nowrap">{formatDate(row.created_at, 'dd.MM.yy HH:mm')}</td>
						<td>{row.pos_name}</td>
						<td><Stars value={row.rating} /></td>
						<td style={{ maxWidth: '280px', whiteSpace: 'normal' }}>{row.note || <span className="chain-muted">—</span>}</td>
						<td className="text-nowrap">
							{row.client_name || '—'}
							{row.client_phone && <div className="chain-muted">{row.client_phone}</div>}
						</td>
						<td className={`text-nowrap ${row.cheque_found ? 'chain-click' : ''}`} onClick={() => onCheque(row)}
							title={row.cheque_found ? t('chain_open_cheque') : t('chain_cheque_not_found')}>
							{row.cheque_found
								? <span className="text-primary">№{row.cheque_number}<div className="chain-muted">{formatMoney(Math.round(row.total_amount_service || 0))}</div></span>
								: <span className="chain-muted">—</span>}
						</td>
						{!compact && <td>{row.waiter_name || '—'}</td>}
						{!compact && <td>{row.courier_name || '—'}</td>}
						{!compact && (
							<td style={{ maxWidth: '240px', whiteSpace: 'normal' }}>
								{row.solution_date
									? <>{row.solution || '✓'}<div className="chain-muted">{formatDate(row.solution_date, 'dd.MM.yy HH:mm')}</div></>
									: row.rating <= 2 ? <span style={{ color: '#f06548' }}>{t('chain_bad_unsolved')}</span> : '—'}
							</td>
						)}
					</tr>
				))}
			</tbody>
		</table>
	)
}

// Средняя по дням (линия) и число оценок (столбцы)
function DailyChart({ daily }) {
	const { t } = useTranslation()
	const days = daily?.days || []
	const series = [
		{ name: t('chain_ratings_count'), type: 'column', data: daily?.ratings || [] },
		{ name: t('average_rating'), type: 'line', data: daily?.avg || [] },
	]
	const options = {
		chart: { type: 'line', toolbar: { show: false }, zoom: { enabled: false } },
		colors: ['rgba(91, 115, 232, 0.55)', '#f59f00'],
		stroke: { width: [0, 3], curve: 'smooth' },
		dataLabels: { enabled: false },
		legend: { position: 'bottom', fontSize: '12px' },
		grid: { borderColor: '#eef2f6', strokeDashArray: 3 },
		markers: { size: [0, 3] },
		xaxis: {
			categories: days.map(day => day.slice(8, 10) + '.' + day.slice(5, 7)),
			tickAmount: Math.min(days.length, 15),
			labels: { rotate: 0, hideOverlappingLabels: true },
		},
		yaxis: [
			{ labels: { formatter: value => formatMoney(Math.round(value)) } },
			{ opposite: true, min: 1, max: 5, tickAmount: 4, labels: { formatter: value => value === null || value === undefined ? '' : Number(value).toFixed(1) } },
		],
		tooltip: { shared: true },
	}

	return (
		<div className="card chain-card mb-0 h-100">
			<div className="card-body">
				<div className="chain-card-title"><h5>{t('chain_by_days')}</h5></div>
				{days.length > 0 && <ReactApexChart type="line" height={340} series={series} options={options} />}
			</div>
		</div>
	)
}

export default ChequeRating
